// src/components/PieceStatsPanel.tsx
import React from "react";
import { Position, Piece } from "../types/Board";
import { getFileLabel, getRankLabel } from "../utils/gameHelpers.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
import { PIECE_VALUES } from "../data/pieceValues.ts";

interface PieceStatsPanelProps {
  halfmoveClock: number;
  fiftyMoveLimit: number;
  seventyFiveMoveLimit: number;
  totalPieces: number;
  whiteCount: number;
  blackCount: number;
  whiteValue: number;
  blackValue: number;
  selectedSquare: Position | null;
  boardRows: number;
  pieces: Map<string, Piece>;
}

export const PieceStatsPanel: React.FC<PieceStatsPanelProps> = ({
  halfmoveClock,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  totalPieces,
  whiteCount,
  blackCount,
  whiteValue,
  blackValue,
  selectedSquare,
  boardRows,
  pieces,
}) => {
  // Determine color based on move rule counter proximity to limit
  const getMoveRuleColor = () => {
    const limit = seventyFiveMoveLimit * 2;
    if (halfmoveClock >= limit - 20) return "#d32f2f"; // Red - close to auto-draw
    if (halfmoveClock >= fiftyMoveLimit * 2) return "#ff9800"; // Orange - can claim draw
    return "#333"; // Normal
  };

  return (
    <div className="piece-stats-panel">
      {/* Move Rule Counter */}
      <div style={{ fontSize: "14px", color: getMoveRuleColor() }}>
        <strong>
          Move Rule
          <br />
          Counter:
        </strong>{" "}
        {halfmoveClock}/{seventyFiveMoveLimit * 2}
        {halfmoveClock >= seventyFiveMoveLimit * 2 - 20 && (
          <span style={{ fontSize: "12px" }}> ⚠️</span>
        )}
      </div>

      {/* Total Pieces on Board */}
      <div style={{ fontSize: "14px", color: "#333" }}>
        <strong>
          Pieces on
          <br />
          Board:
        </strong>{" "}
        {totalPieces}
      </div>

      {/* White Pieces Count */}
      <div style={{ fontSize: "14px", color: "#333" }}>
        <strong>
          White
          <br />
          Pieces:
        </strong>{" "}
        {whiteCount}
      </div>

      {/* White Material Value */}
      <div style={{ fontSize: "14px", color: "#333" }}>
        <strong>
          White
          <br />
          Value:
        </strong>{" "}
        {whiteValue.toFixed(2)}
      </div>

      {/* Black Pieces Count */}
      <div style={{ fontSize: "14px", color: "#333" }}>
        <strong>
          Black
          <br />
          Pieces:
        </strong>{" "}
        {blackCount}
      </div>

      {/* Black Material Value */}
      <div style={{ fontSize: "14px", color: "#333" }}>
        <strong>
          Black
          <br />
          Value:
        </strong>{" "}
        {blackValue.toFixed(2)}
      </div>

      {/* Selected Piece Display */}
      <div
        style={{
          fontSize: "14px",
          color: selectedSquare ? "#333" : "#999",
          minHeight: "40px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <strong>Selected Piece:</strong>
        <span style={{ whiteSpace: "nowrap" }}>
          {selectedSquare
            ? (() => {
                const squareKey = `${selectedSquare.row}-${selectedSquare.col}`;
                const piece = pieces.get(squareKey);
                const position = `${getFileLabel(selectedSquare.col)}${getRankLabel(
                  selectedSquare.row,
                  boardRows
                )}`;

                if (piece) {
                  const pieceType = piece.type?.toLowerCase() || "";
                  const pieceDef = PIECE_DEFINITIONS[pieceType];
                  const abbreviation = pieceDef?.abbreviation?.toUpperCase() ||
                    pieceType.substring(0, 3).toUpperCase();

                  // Get value based on board size from PIECE_VALUES
                  const boardSizeKey = `${boardRows}×${boardRows}`;
                  const pieceValueData = PIECE_VALUES[abbreviation];
                  let value = pieceDef?.baseValue ?? 0;

                  if (pieceValueData) {
                    const sizeValue = pieceValueData.find(v => v.boardSize === boardSizeKey);
                    if (sizeValue) {
                      value = sizeValue.value;
                    }
                  }

                  return `${position}-${abbreviation}-${value.toFixed(2)}`;
                }
                return position;
              })()
            : "None"}
        </span>
      </div>
    </div>
  );
};

export default PieceStatsPanel;
